<template>
  <div data-private class="flex h-full">
    <PagesExplorer :nodes="nodes" :loading="loading" :error="error || ''" />
    <div class="flex-1 h-full overflow-auto bg-gray-100" style="scrollbar-gutter: stable">
      <PagesAdmin v-if="subject === 'admin'" />
      <PagesFolder v-else-if="subject === 'folder'" :key="`folder-${stem}`" :stem="stem" :pages="mine" />
      <PageHost v-else :key="`${subject}-${subjectId}`" :subject="subject" :subject-id="subjectId" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PageHost from './PageHost.vue';
import PagesAdmin from './PagesAdmin.vue';
import PagesFolder from './PagesFolder.vue';
import PagesExplorer from './PagesExplorer.vue';
import { childFolders, domainFolder, folderPages, folderUrl, pageUrl } from '@/utils/pageFolders';

export default {
  name: 'PagesView',
  components: { PageHost, PagesAdmin, PagesFolder, PagesExplorer },
  provide() {
    return { refreshMine: this.fetchMine };
  },
  props: {
    subject: { type: String, default: 'overall' },
    subjectId: { type: Number, default: null },
    stem: { type: String, default: '' },
  },
  data() {
    return { domains: [], mine: [], loading: false, error: null };
  },
  computed: {
    nodes() {
      const tree = this.mineChildren('');
      this.domains.forEach((domain) => {
        const label = domainFolder(domain.name);
        let folder = tree.find((node) => node.key === `folder-${label}`);
        if (!folder) {
          folder = { key: `folder-${label}`, label, icon: ['fas', 'folder'], kind: 'folder', children: [] };
          tree.push(folder);
        }
        folder.to = `/pages/domain/${domain.id}`;
        folder.sensitive = false;
        const metrics = (domain.composable_metrics || [])
          .map((link) => ({ key: `metric-${domain.id}-${link.metric_id}`, label: link.metric_name, to: `/pages/metric/${link.metric_id}`, icon: ['fas', 'chart-bar'], kind: 'metric' }))
          .sort((a, b) => a.label.localeCompare(b.label));
        const folders = folder.children.filter((node) => node.kind === 'folder');
        const pages = folder.children.filter((node) => node.kind === 'page');
        folder.children = [...folders, ...metrics, ...pages];
      });
      const folders = tree.filter((node) => node.kind === 'folder').sort((a, b) => a.label.localeCompare(b.label));
      const pages = tree.filter((node) => node.kind === 'page');
      return [...folders, ...pages];
    },
  },
  mounted() {
    this.fetchDomains();
    this.fetchMine();
  },
  methods: {
    visibility(page) {
      if (page.shared || page.category) return { icon: ['fas', 'globe'], tone: 'text-primary-600', hint: 'Public: everyone in the tenant can open it' };
      return { icon: ['fas', 'user'], tone: 'text-gray-700', hint: 'Just yours: private to you' };
    },
    mineChildren(stem) {
      const folders = childFolders(this.mine, stem).map((folder) => ({
        key: `folder-${folder.stem}`, label: folder.name, to: folderUrl(folder.stem), icon: ['fas', 'folder'], sensitive: true, kind: 'folder', children: this.mineChildren(folder.stem),
      }));
      const pages = folderPages(this.mine, stem).map((page) => ({
        key: `page-${page.id}`, label: page.title, to: pageUrl(page), sensitive: true, kind: 'page', ...this.visibility(page),
      }));
      return [...folders, ...pages];
    },
    async fetchDomains() {
      try {
        const { data } = await axios.get('/api/performancedomains/');
        this.domains = data;
      } catch {
        this.error = 'Failed to load performance domains';
      }
    },
    async fetchMine() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/impact_analytics/agent-pages/');
        this.mine = data.filter((page) => (page.mine || page.shared || page.category) && !page.subject_type);
      } catch {
        this.error = 'Failed to load pages';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
